import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Eye,
  CalendarDays,
  TrendingUp,
  CalendarRange,
  Inbox,
  MailWarning,
  Loader2,
} from "lucide-react";
import { api } from "@/lib/api";

const CARDS = [
  { key: "total", label: "Vizitatori unici (total)", icon: Eye, color: "#FF5A1F" },
  { key: "today", label: "Vizitatori unici azi", icon: CalendarDays, color: "#16A34A" },
  { key: "last7", label: "Unici — ultimele 7 zile", icon: TrendingUp, color: "#2563EB" },
  { key: "last30", label: "Unici — ultimele 30 zile", icon: CalendarRange, color: "#9333EA" },
  { key: "total_messages", label: "Mesaje primite", icon: Inbox, color: "#0F1E36" },
  { key: "unread", label: "Mesaje necitite", icon: MailWarning, color: "#DC2626" },
];

export default function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/admin/analytics")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Eroare la încărcarea statisticilor"))
      .finally(() => setLoading(false));
  }, []);

  if (loading)
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );

  if (!data) return null;

  const series = data.series || [];
  const max = Math.max(1, ...series.map((s) => s.count));
  const fmtDay = (d) => {
    const [, m, day] = d.split("-");
    return `${day}.${m}`;
  };

  return (
    <div className="space-y-6">
      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {CARDS.map((c) => {
          const Icon = c.icon;
          return (
            <div
              key={c.key}
              data-testid={`analytics-card-${c.key}`}
              className="bg-white rounded-2xl border border-[#E2E8F0] p-5 flex items-center gap-4"
            >
              <span
                className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                style={{ backgroundColor: `${c.color}15` }}
              >
                <Icon className="w-6 h-6" style={{ color: c.color }} />
              </span>
              <div className="min-w-0">
                <p className="font-display text-3xl font-extrabold text-[#0F1E36] leading-none">
                  {data[c.key] ?? 0}
                </p>
                <p className="text-sm text-slate-500 mt-1 truncate">{c.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* 14-day chart */}
      <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-lg font-bold text-[#0F1E36]">
            Vizitatori unici — ultimele 14 zile
          </h3>
          <span className="text-xs font-mono text-slate-400">
            max {max}/zi
          </span>
        </div>
        <div className="flex items-end justify-between gap-1.5 h-48" data-testid="analytics-chart">
          {series.map((s) => (
            <div key={s.day} className="flex-1 flex flex-col items-center gap-2 group">
              <div className="w-full flex items-end justify-center h-40">
                <div
                  className="w-full max-w-[34px] rounded-t-md bg-[#FF5A1F] hover:bg-[#E04B14] transition-all duration-200 relative"
                  style={{ height: `${Math.max(4, (s.count / max) * 100)}%` }}
                  title={`${s.count} vizitatori unici`}
                >
                  <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[11px] font-bold text-[#0F1E36] opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    {s.count}
                  </span>
                </div>
              </div>
              <span className="text-[10px] font-mono text-slate-400 rotate-0">
                {fmtDay(s.day)}
              </span>
            </div>
          ))}
        </div>
      </div>

      <p className="text-xs text-slate-400">
        * Vizitatorii unici sunt numărați o singură dată pe zi (pe bază de IP +
        browser). Datele se actualizează în timp real.
      </p>
    </div>
  );
}
